import { useQuery } from '@tanstack/react-query';
import {
  fetchConsolidators,
  fetchConsolidatorSites,
  fetchConsolidatorWhitespace,
  type ConsolidatorsQuery,
  type ConsolidatorsResponse,
  type ConsolidatorSitesResponse,
  type ConsolidatorWhitespaceResponse,
} from './client.js';

// Consolidators ("Who's Who") hook. Keyed on the full query (country +
// subsegment + region), so changing a filter refetches. `enabled` lets the
// panel hold the fetch until its market scope is seeded (mandate / coverage).

export function useConsolidators(query: ConsolidatorsQuery = {}, opts: { enabled?: boolean } = {}) {
  return useQuery<ConsolidatorsResponse, Error>({
    queryKey: ['consolidators', query] as const,
    queryFn: () => fetchConsolidators(query),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

/** Site-point layer for the Map view. Only fetched when the Map view is
 *  active (gated by `enabled`) — it returns thousands of points. */
export function useConsolidatorSites(query: ConsolidatorsQuery, opts: { enabled?: boolean } = {}) {
  return useQuery<ConsolidatorSitesResponse, Error>({
    queryKey: ['consolidator-sites', query] as const,
    queryFn: () => fetchConsolidatorSites(query),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}

/** White-space layer — independents (acquirable). Only fetched when the map +
 *  overlay are both on (gated). Sampled server-side. */
export function useConsolidatorWhitespace(
  query: ConsolidatorsQuery,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<ConsolidatorWhitespaceResponse, Error>({
    queryKey: ['consolidator-whitespace', query] as const,
    queryFn: () => fetchConsolidatorWhitespace(query),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
  });
}
